import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  InputLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import { useAuth } from "../../../../context/AuthContext";

const ActiveContextSelector = ({
  title = "Contexto institucional",
  helperText,
  variant = "card",
}) => {
  const { user, switchContext, loading } = useAuth();
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [selectedInstitutionId, setSelectedInstitutionId] = useState("");
  const [error, setError] = useState("");

  const groupedContexts = useMemo(
    () => (Array.isArray(user?.contextos_agrupados) ? user.contextos_agrupados : []),
    [user?.contextos_agrupados]
  );

  const totalModulares = useMemo(
    () =>
      groupedContexts.reduce(
        (acc, group) => acc + (Array.isArray(group?.modulares) ? group.modulares.length : 0),
        0
      ),
    [groupedContexts]
  );

  const shouldShow = totalModulares > 1;

  const activeGroupId = user?.contexto_activo?.grupo_id || "";

  const selectedGroup = useMemo(
    () =>
      groupedContexts.find(
        (group) => String(group?.grupo_id) === String(selectedGroupId || activeGroupId)
      ) || null,
    [groupedContexts, selectedGroupId, activeGroupId]
  );

  const selectedGroupModulares = Array.isArray(selectedGroup?.modulares)
    ? selectedGroup.modulares
    : [];

  const shouldShowGroupSelector = groupedContexts.length > 1;
  const shouldShowModularSelector =
    !shouldShowGroupSelector || selectedGroupModulares.length > 1;

  useEffect(() => {
    setSelectedGroupId(user?.contexto_activo?.grupo_id || groupedContexts[0]?.grupo_id || "");
    setSelectedInstitutionId(user?.institucion?.id || "");
  }, [user?.institucion?.id, user?.contexto_activo?.grupo_id, groupedContexts]);

  if (!shouldShow) return null;

  const getFriendlyRoleName = (roleName = "") => {
    const normalized = String(roleName || "").toUpperCase();
    if (normalized === "DIRECTOR") return "Docente Admin";
    if (normalized === "PROFESOR") return "Docente";
    if (normalized === "ADMIN") return "Administrador";
    return normalized;
  };

  const formatGroupLabel = (group) => {
    const levels = Array.isArray(group?.niveles) ? group.niveles.filter(Boolean) : [];
    const suffix =
      levels.length > 0 && levels.length <= 2 ? ` (${levels.join(" / ")})` : "";
    return `${group?.nombre_ie || "Institución"}${suffix}`;
  };

  const formatModularLabel = (contexto) =>
    [
      contexto?.nivel_modalidad || "Sin nivel",
      getFriendlyRoleName(contexto?.rol_nombre),
    ]
      .filter(Boolean)
      .join(" · ");

  const executeSwitchContext = async (nextInstitutionId) => {
    setSelectedInstitutionId(nextInstitutionId);
    setError("");

    const result = await switchContext(nextInstitutionId);
    if (!result?.authenticated) {
      setError("No se pudo cambiar el contexto institucional.");
    }
  };

  const handleInstitutionChange = async (event) => {
    const nextInstitutionId = event.target.value;
    await executeSwitchContext(nextInstitutionId);
  };

  const handleGroupChange = async (event) => {
    const nextGroupId = event.target.value;
    setSelectedGroupId(nextGroupId);
    setError("");

    const nextGroup = groupedContexts.find(
      (group) => String(group?.grupo_id) === String(nextGroupId)
    );
    const nextModulares = Array.isArray(nextGroup?.modulares) ? nextGroup.modulares : [];

    if (nextModulares.length === 1) {
      await executeSwitchContext(nextModulares[0].institucion_id);
      return;
    }

    const activeInstitutionInGroup = nextModulares.find((item) => item?.activo);
    setSelectedInstitutionId(
      activeInstitutionInGroup?.institucion_id || nextModulares[0]?.institucion_id || ""
    );
  };

  const baseSelectSx =
    variant === "step3"
      ? {
          "& .MuiOutlinedInput-root": {
            borderRadius: "999px",
            backgroundColor: "#fff",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#dedede",
          },
          "& .MuiSelect-select": {
            minHeight: "52px !important",
            padding: "14px 52px 14px 22px !important",
            display: "flex",
            alignItems: "center",
            color: "#6f6f6f",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
          "& .MuiSelect-icon": {
            color: "#9c9c9c",
            right: 16,
          },
          "& .MuiFormHelperText-root": {
            mx: 1,
          },
        }
      : {
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            backgroundColor: "#fff",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#cbd5e1",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#94a3b8",
          },
          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#94a3b8",
            borderWidth: 1.5,
          },
          "& .MuiSelect-select": {
            minHeight: "24px !important",
            padding: "12px 42px 12px 16px !important",
            color: "#334155",
            fontWeight: 600,
            fontSize: "0.95rem",
            display: "flex",
            alignItems: "center",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
          "& .MuiSelect-icon": {
            color: "#64748b",
            right: 14,
          },
          "& .MuiFormHelperText-root": {
            mx: 0,
            mt: 1.5,
            color: "#64748b",
            fontSize: "0.85rem",
            lineHeight: 1.4,
          },
        };

  const wrapperSx =
    variant === "step3"
      ? {
          width: "100%",
          maxWidth: 560,
          mb: 3,
          p: 2.25,
          borderRadius: 3,
          border: "1px solid rgba(15,23,42,0.08)",
          backgroundColor: "#ffffff",
          boxShadow: "0 10px 24px rgba(2,6,23,0.04)",
        }
      : {
          width: "100%",
          mb: 4,
          p: 2.5,
          borderRadius: "12px",
          border: "1px solid #f1f5f9",
          backgroundColor: "#f8fafc",
          boxShadow: "none",
        };

  const selectLabelSx =
    variant === "step3"
      ? {
          position: "static",
          transform: "none",
          fontSize: "0.82rem",
          color: "#8f8f8f",
        }
      : {
          position: "static",
          transform: "none",
          fontSize: "0.82rem",
          color: "#64748b",
          fontWeight: 600,
        };

  const resolvedHelperText =
    helperText ||
    "Selecciona la institución y el nivel con el que trabajarás en este flujo.";

  return (
    <Box sx={wrapperSx}>
      <Stack spacing={1.5}>
        {variant === "card" ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <ApartmentRoundedIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
            <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#1e293b" }}>
              {title}
            </Typography>
          </Box>
        ) : (
          <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#111827" }}>
            {title}
          </Typography>
        )}

        {shouldShowGroupSelector && (
          <Box>
            <InputLabel shrink sx={selectLabelSx}>
              Institución
            </InputLabel>
            <TextField
              select
              fullWidth
              label={undefined}
              value={selectedGroupId}
              disabled={loading}
              onChange={handleGroupChange}
              sx={baseSelectSx}
            >
              {groupedContexts.map((group) => (
                <MenuItem key={group.grupo_id} value={group.grupo_id}>
                  {formatGroupLabel(group)}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}

        {shouldShowModularSelector && (
          <Box>
            <InputLabel shrink sx={selectLabelSx}>
              {shouldShowGroupSelector ? "Nivel / rol" : "Institución activa"}
            </InputLabel>
            <TextField
              select
              fullWidth
              label={undefined}
              value={selectedInstitutionId}
              disabled={loading}
              onChange={handleInstitutionChange}
              helperText={resolvedHelperText}
              sx={baseSelectSx}
            >
              {selectedGroupModulares.map((contexto) => (
                <MenuItem
                  key={`${contexto.institucion_id}-${contexto.rol_nombre}`}
                  value={contexto.institucion_id}
                >
                  {formatModularLabel(contexto)}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}

        {!shouldShowModularSelector && shouldShowGroupSelector && (
          <Typography
            sx={{
              fontSize: "0.85rem",
              color: "#64748b",
              lineHeight: 1.45,
            }}
          >
            {resolvedHelperText}
          </Typography>
        )}

        {loading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={16} thickness={5} sx={{ color: "#00bcd4" }} />
            <Typography sx={{ fontSize: "0.85rem", color: "#64748b" }}>
              Actualizando contexto...
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" variant="outlined" sx={{ py: 0, '& .MuiAlert-message': { fontSize: '0.85rem' }}}>
            {error}
          </Alert>
        )}
      </Stack>
    </Box>
  );
};

export default ActiveContextSelector;
